/**
 * The quote record — `/countries/:cc/quotes/:quoteId` — in the house language
 * (WEB TIER A · WT4, `record.html` approved verbatim).
 *
 * =============================================================================
 * ONE READING ORDER: WHAT THIS IS → WHERE IT IS IN ITS LIFE → THE LINES → THE
 * PROPERTIES
 * =============================================================================
 * The approved record's structure, kept exactly:
 *  · IDENTITY CARD — h1 customer with the grand total ADJACENT (the register's
 *    own arrangement: a reader who clicked a worklist row lands on the same two
 *    fields in the same order) + status pill; the doc-no chip with Copy number;
 *    the PDF acts; and the provenance sub-line ending in the status map's own
 *    authored meaning sentence.
 *  · LIFECYCLE CARD — three numbered steps (Raised / Sent / Confirmed) with
 *    done/now/next states. THE ACT BELONGS TO THE CURRENT STEP: Confirm and
 *    Mark lost sit inside the step that owns them, not in a row of six
 *    competing header buttons. The current step is a 3px `info/text` LEFT EDGE,
 *    not a fill (a status tint would drop the card's own quiet text to ~4.1);
 *    an inert future step is a DASHED rim with its words at full contrast, and
 *    step 3 states its per-TYPE fact although it has not happened — that is the
 *    fact the reader is deciding on (§B5: a CRF confirm raises a sales order; a
 *    Tally Sales confirm stops).
 *  · COLS — the lines card (header → 6-col table → right-aligned totals stack,
 *    ONE card) beside a 360px aside of kv cards: Document (with the
 *    tax-provenance help line), Customer, History with honest no-value dashes.
 *
 * The identity line is INSIDE a card, deliberately: on the authored page canvas
 * `text/secondary` measures 4.10; on `bg/surface` it is 4.64. Every quiet line
 * on this page sits inside a card for that one reason.
 *
 * WT4's ONE DIALOG MIGRATION: the local `DialogShell` (a hand-wired Radix
 * composition duplicating `ConfirmDialog`'s config — its own W3 history is in
 * the vendored file's header) is retired for the vendored `Modal`
 * (`components/ui/Modal.tsx`), which carries the same focus trap, Escape
 * handler, busy-guard and scrim as one source of truth.
 *
 * CALLOUT — THIS RULING WAS AMENDED AT PROVE TIME, AND THE REASON IS A NUMBER.
 * WT4 recorded that `ConfirmDialog` is "NOT migrated here". That left the
 * highest-stakes control in the product — Confirm, which raises a sales order
 * and ends re-pricing — wearing `bg-danger-fg/90` hover and `/80` active. Those
 * alpha fades composite against the SURFACE, so they read differently per mode.
 * Re-measured (white on the composite, WCAG 2.x floored 2dp, the convention
 * `tokens.contrast.test.ts` uses):
 *
 *            rest      hover           active
 *   light    5.52 ✓    4.83 ✓          4.09 ✗  BELOW AA — and FALLING
 *   dark     5.52 ✓    6.28 ✓          7.13 ✓  RISING
 *
 * The two modes move in OPPOSITE directions from one class string, which is
 * precisely the defect signature D1a was authored to eliminate — and it is
 * invisible to anyone testing in dark. `ConfirmModal` was built in WT2
 * expressly to match `ConfirmDialog`'s API so this migration would be
 * mechanical, and it carries the authored `bg-brand-hover` (#b91f1f, white =
 * 6.40) for BOTH hover and active: contrast rises, mode-invariantly, with no
 * opacity modifier. The swap is an import and a tag name.
 *
 * `admin/ConfirmDialog` is repaired in place in its own commit for its seven
 * remaining admin callers; its migration to `ConfirmModal` stays on the ledger
 * (WEB-TIER-A-LEDGER item 1) because that is a wiring change, not a contrast
 * one. Pinned in `tokens.contrast.test.ts`.
 *
 * =============================================================================
 * D5 — AN ACTION THE CALLER CANNOT PERFORM IS ABSENT, NOT DISABLED
 * =============================================================================
 * Unchanged through the restyle. `quotes.send`, `quotes.confirm`,
 * `quotes.discount.request`, `quotes.discount.approve` and `amounts.view`
 * (which both PDF routes require in addition to `quotes.view`, decision U4-1)
 * are five keys held by different roles. The worked example is MANAGEMENT
 * (`apps/api/src/seeds/role-matrix.json`): they hold view/confirm/approve and
 * NOT create/send — so they see the document, see "Mark lost", and see no
 * "Mark as sent"; that is the truth about their authority, where greyed-out
 * controls would read as a broken screen. A PERMISSION gate hides; a STATE
 * gate shows and explains — the lifecycle card now IS that explanation: the
 * absent act's step says where the document is.
 *
 * =============================================================================
 * WHAT THIS SCREEN CANNOT SHOW, AND SAYS SO (all unchanged)
 * =============================================================================
 *  · OQ-8 — no read route for discount requests: the card states it and shows
 *    only what a POST returned this session.
 *  · OQ-6 — a discount can only be requested on a DRAFT; the sent case says why.
 *  · OQ-4 — an amount discount cannot be approved (501); the form takes a
 *    percentage only.
 *  · OQ-U3-3 — `quotes` has no `lost_reason` column; the lost banner says where
 *    the reason lives (the `quote.lost` event) instead of an empty field.
 *  · The record.html aside draws City/State/GSTIN/Phone from its fixture's
 *    party; the quote wire carries none of those fields, so the Customer card
 *    prints what the wire has and nothing invented (filed — ledger item 12).
 *
 * =============================================================================
 * MASKING (rules untouched)
 * =============================================================================
 * A masked seat sees the ••• marker in the money's OWN slot at the money's own
 * size (the identity figure is `ServerAmount` at `t-h1`; the totals stack rows
 * at theirs — zero reflow), and a PERMANENT in-card banner naming the state,
 * the identity reading it, and the remedy. Never hover-only.
 *
 * =============================================================================
 * THE PDF IS FETCHED, NOT LINKED
 * =============================================================================
 * `GET /quotes/:id/pdf` is behind the JWT guard, so `<a href>` gets 401. The
 * bytes come through the api client (`fetchQuotePdf` → `fetchBinary`) and
 * become an object URL, revoked immediately after the click: a `blob:` URL is
 * a live handle to a customer's priced document.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Copy, Download, FileText } from 'lucide-react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import {
  Alert,
  Button,
  KvList,
  KvRow,
  ConfirmModal,
  Modal,
  RecordCard,
  TextField,
  TotalsBlock,
} from '../../components/ui';
import type { TotalsRowSpec } from '../../components/ui';
// ConfirmModal, not `admin/ConfirmDialog` — see the header's confirm ruling.
// WT5 — `inquiries/panel` died with the slide-over kill; TextAreaField moved
// to `components/ui` (import repoint only, byte-identical component).
import { TextAreaField } from '../../components/ui/TextAreaField';
import { formatDateLong, formatMoney, isMaskedAmount, NO_VALUE } from '../inquiries/format';
import { useToast } from '../inquiries/toast';
import { countryProfile, customerOriginLabel } from '../../domain/countries';
import {
  QUOTE_STATUS_MAP,
  QUOTE_TYPE_LABELS,
  daysSince,
  docToneClasses,
  isAgtDocNo,
  statusLabelInSentence,
} from '../../domain/quotes';
import type { CountryCode } from '../../domain/countries';
import type { DocTone } from '../../domain/quotes';
import { cn } from '../../lib/cn';
import { DocumentLineTable } from './LineTable';
import { ServerAmount, ServerDeduction } from './money';
import { QuoteStatusPill } from './status';
import { DocumentSkeletonRecord } from './states';
import { clockBehindOf, clockBehindSentence, quoteErrorText } from './errors';
import type { ClockBehind } from './errors';
import { fetchQuotePdf } from './api';
import type { DiscountRequestDto, QuoteRecordDto } from './api';
import { ageLabel } from './worklist';
import {
  useAnnulQuote,
  useConfirmQuote,
  useDecideDiscount,
  useMarkQuoteLost,
  useQuoteRecord,
  useRenderQuotePdf,
  useRequestDiscount,
  useSendQuote,
} from './use-quotes';

export function QuoteRecordPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { quoteId } = useParams<{ quoteId: string }>();
  const { can } = useSession();
  const toast = useToast();

  const canView = can(countryCode, 'quotes.view');
  const id = quoteId ?? null;
  const record = useQuoteRecord(id, canView);

  if (!canView) {
    return (
      <div className="p-6">
        <Alert tone="danger" title="This quote is not available to this account">
          Viewing quotes in {countryCode} needs the Quotes permission.
        </Alert>
      </div>
    );
  }

  if (record.isPending) {
    return (
      <div className="p-6">
        <DocumentSkeletonRecord />
      </div>
    );
  }

  if (record.isError || record.data === undefined) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="This quote could not be opened">
          {quoteErrorText(record.error)}
        </Alert>
        <Link
          to={`/countries/${countryCode}/quotes`}
          className="w-fit text-body13 text-fg underline underline-offset-2 hover:decoration-2"
        >
          Back to the quote list
        </Link>
      </div>
    );
  }

  return (
    <QuoteRecord
      key={record.data.id}
      countryCode={countryCode}
      quote={record.data}
      onToast={(text, tone) => toast.push({ text, tone })}
    />
  );
}

/* ────────────────────────────────────────────────────────────── the record ── */

function QuoteRecord({
  countryCode,
  quote,
  onToast,
}: {
  /** Narrowed, not `string`: `can()` takes a `CountryCode` and this is the gate. */
  countryCode: CountryCode;
  quote: QuoteRecordDto;
  onToast: (text: string, tone?: 'success' | 'error') => void;
}): React.JSX.Element {
  const { can, me } = useSession();

  const canSend = can(countryCode, 'quotes.send');
  const canConfirm = can(countryCode, 'quotes.confirm');
  const canRequestDiscount = can(countryCode, 'quotes.discount.request');
  const canApproveDiscount = can(countryCode, 'quotes.discount.approve');
  // Decision U4-1: BOTH PDF routes are `@RequirePerm('quotes.view','amounts.view')`,
  // because a PDF is a stream of money the masking interceptor structurally cannot
  // mask. Without this key the buttons are absent rather than 403-on-click.
  const canPdf = can(countryCode, 'quotes.view') && can(countryCode, 'amounts.view');
  // AGT design §3 U2, D7: annul exists only in a SAF-T(AO) country, on a document numbered in
  // its AGT série (G1: a quote sent before the série opened keeps today's exits), graded
  // quotes.confirm AND config.manage (G6) — absent, not disabled, for anyone else. The step-2
  // actions add "sent".
  const canAnnul =
    countryProfile(countryCode).saftAo &&
    isAgtDocNo(quote.doc_no) &&
    canConfirm &&
    can(countryCode, 'config.manage');

  const [lostOpen, setLostOpen] = useState(false);
  const [annulOpen, setAnnulOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** DE 74/19 33 c) (AGT U2): the send or confirm the server refused on its clock, awaiting the operator's word. */
  const [clockRetry, setClockRetry] = useState<{ act: 'send' | 'confirm'; clock: ClockBehind } | null>(null);
  /**
   * What the two discount POSTs told us THIS SESSION.
   *
   * OQ-8: there is no read route, so this is the entire universe of what the
   * screen can know about a discount request. It is deliberately not persisted
   * and deliberately not presented as "the current state" — a reload empties it,
   * which is honest, because a reload really does leave this client knowing
   * nothing about pending requests.
   */
  const [lastRequest, setLastRequest] = useState<DiscountRequestDto | null>(null);

  const send = useSendQuote(countryCode, quote.id);
  const confirm = useConfirmQuote(countryCode, quote.id);
  const lost = useMarkQuoteLost(countryCode, quote.id);
  const annul = useAnnulQuote(countryCode, quote.id);
  const requestDiscount = useRequestDiscount(countryCode, quote.id);
  const decide = useDecideDiscount(countryCode, quote.id);
  const render = useRenderQuotePdf(countryCode, quote.id);
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const descriptor = QUOTE_STATUS_MAP[quote.status];
  const isDraft = quote.status === 'draft';
  const isSent = quote.status === 'sent';
  const isConfirmed = quote.status === 'confirmed';
  /** Terminal WITHOUT completing the ladder — `confirmed` is the ladder's own end. */
  const isClosedTerminal =
    quote.status === 'lost' || quote.status === 'expired' || quote.status === 'annulled';

  const busy =
    send.isPending ||
    confirm.isPending ||
    lost.isPending ||
    annul.isPending ||
    requestDiscount.isPending ||
    decide.isPending ||
    render.isPending ||
    downloading;

  const download = async (): Promise<void> => {
    setPdfError(null);
    setDownloading(true);
    try {
      const file = await fetchQuotePdf(quote.id);
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.filename ?? `${quote.doc_no}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoked immediately: a `blob:` URL is a live handle to a customer's priced
      // document, and the browser has already taken the bytes it needs.
      URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError(quoteErrorText(error));
    } finally {
      setDownloading(false);
    }
  };

  const copyNumber = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(quote.doc_no);
      onToast(`${quote.doc_no} copied`);
    } catch {
      onToast('The number could not be copied — select it from the chip instead.', 'error');
    }
  };
  /*
   * AGT séries (design §3 U1): in a country numbering under an AGT série a draft carries the
   * placeholder `DRAFT-<id>` — not a document number — and is numbered when it is sent
   * (`OR AOQT2026/1`, the only doc_no shape with a space). So there is nothing to copy yet,
   * and the Lifecycle card says when the number comes. A legacy-numbered saft_ao draft is
   * renumbered into the série if it is sent after the série opens (G2), so it says that.
   */
  const unnumberedDraft = quote.doc_no.startsWith('DRAFT-');
  const legacyNote = `The number was issued from the ${countryCode} series when this quote was first saved.`;
  const numberingNote = !countryProfile(countryCode).saftAo
    ? legacyNote
    : unnumberedDraft
      ? `This draft has no number yet: the ${countryCode} AGT number is issued when it is sent.`
      : isAgtDocNo(quote.doc_no)
        ? `The number was issued from the ${countryCode} AGT série when this quote was sent.`
        : isDraft
          ? `${legacyNote} If the ${countryCode} AGT série is open when it is sent, it is renumbered there.`
          : legacyNote;

  const customer = quote.party_name ?? 'Unknown customer';
  const age = daysSince(quote.created_at);
  /*
   * The provenance sub-line — the identity card's last line, ending in the
   * authored meaning sentence from `domain/quotes.ts` (the map's own words,
   * never a paraphrase). The clock claims that used to trail the old status
   * line (`· sent {date} · confirmed {date}`) now live where the mockup puts
   * them: on the lifecycle steps and in the History card.
   */
  const provenance = [
    QUOTE_TYPE_LABELS[quote.type],
    `raised ${formatDateLong(quote.created_at)}`,
    ...(age === null ? [] : [`${ageLabel(age)} ago`]),
    `priced in ${quote.currency}`,
    ...(quote.tax_group_name === null ? [] : [`tax group ${quote.tax_group_name}`]),
    descriptor.meaning ?? descriptor.label,
  ].join(' · ');

  /*
   * MASKED SEAT — the register's rule, applied to the record: the marker sits
   * in every money slot at the money's own size (`ServerAmount` renders it),
   * and this banner is the PERMANENT carrier of state + identity + remedy —
   * never hover-only. The interceptor masks every money field together, so the
   * grand total is a faithful proxy for the whole document.
   */
  const maskedSeat = isMaskedAmount(quote.grand_total);
  const maskedRole = me?.scopes.find((scope) => scope.c === countryCode)?.r ?? null;

  /* ── lifecycle derivation — the three steps and who owns the act ────────── */

  const step1State: StepState = isDraft ? 'now' : 'done';
  const step2State: StepState = isDraft
    ? 'next'
    : isSent
      ? 'now'
      : isConfirmed || quote.sent_at !== null
        ? 'done'
        : 'next';
  const step3State: StepState = isConfirmed ? 'done' : 'next';

  const step2Desc = isDraft
    ? 'Sending issues the document to the customer and freezes the lines.'
    : isSent
      ? quote.sent_at === null
        ? 'The document is with the customer — the send date was not recorded on this row.'
        : `Sent ${formatDateLong(quote.sent_at)}. The document is in the customer’s hands.`
      : step2State === 'done'
        ? `Sent ${formatDateLong(quote.sent_at)}.`
        : `Never sent — the quote closed as ${statusLabelInSentence(descriptor.label)}.`;

  /*
   * THE PER-TYPE STEP-3 SENTENCE (§B5) — stated although it has not happened,
   * because it is the fact the reader is deciding on; and stated in the past
   * tense once it has, which is where the old status line's appended
   * order-was-created half now lives.
   */
  /*
   * ⚠ THREE ARMS, NOT TWO (C11/IQ-12b, owner ruling Q1). This was a two-way branch on
   * `type === 'tally_sales'`, whose `else` said "a sales order was created". With `mixed`
   * a real and now REACHABLE value, that `else` would have told the reader of a mixed
   * document that an order exists for it — and none does. A blacklist branch on a
   * vocabulary that grew is exactly how a screen comes to state something false.
   *
   * The mixed sentence says WHY rather than just what: an operator looking at a document
   * that sells a licence and customisation work reasonably expects the customisation half
   * to be scheduled, and the honest answer is that this build does not write a part-order.
   */
  const orderOutcomePast =
    quote.type === 'crf'
      ? 'A sales order was created from this quote.'
      : quote.type === 'tally_sales'
        ? 'No sales order was created — a Tally Sales quote is raised in Tally by hand.'
        : 'No sales order was created. This document sells a Tally licence alongside other work: the licence is raised in Tally by hand, and a part-order for the rest is not something this build writes.';

  const orderOutcomeFuture =
    quote.type === 'crf'
      ? 'This is a CRF customisation quote, so confirming it creates a sales order with an immutable copy of every line and total.'
      : quote.type === 'tally_sales'
        ? 'This is a Tally Sales quote, so confirming marks it confirmed and stops — the licence is raised in Tally by hand.'
        : 'This document sells a Tally licence alongside other work, so confirming marks it confirmed and stops. No sales order is created for either half.';

  const step3Desc = isConfirmed
    ? (quote.confirmed_at === null
        ? ''
        : `Confirmed ${formatDateLong(quote.confirmed_at)}. `) + orderOutcomePast
    : isClosedTerminal
      ? `Not reached — the quote closed as ${statusLabelInSentence(descriptor.label)}.`
      : orderOutcomeFuture;

  /* ── the acts, living on the step that owns them (D5: absent, not disabled) ── */

  const markSent = (confirmClock = false): void =>
    send.mutate(confirmClock, {
      onSuccess: () => {
        setClockRetry(null);
        onToast(`${quote.doc_no} marked as sent`);
      },
      onError: (error) => {
        const clock = clockBehindOf(error);
        setClockRetry(clock === null ? null : { act: 'send', clock });
        if (clock === null) onToast(quoteErrorText(error), 'error');
      },
    });

  const confirmNow = (confirmClock = false): void =>
    confirm.mutate(confirmClock, {
      onSuccess: (result) => {
        setConfirmOpen(false);
        setClockRetry(null);
        /*
         * `sales_order` IS NULL FOR A TALLY SALES QUOTE (§B5). Branching on the
         * RESPONSE rather than on `quote.type` is deliberate: the server decides,
         * and if it ever creates an order for a type this client thinks it should
         * not, the operator is told the truth — the order's number — instead of a
         * confident sentence the client made up.
         */
        onToast(
          result.sales_order === null
            ? `${result.quote.doc_no} confirmed. No sales order — raise the licence in Tally.`
            : `${result.sales_order.doc_no} created — awaiting Tally sync (Phase 3b).`,
        );
      },
      onError: (error) => {
        setConfirmOpen(false);
        const clock = clockBehindOf(error);
        setClockRetry(clock === null ? null : { act: 'confirm', clock });
        if (clock === null) onToast(quoteErrorText(error), 'error');
      },
    });

  const step1Actions =
    isDraft && (canSend || canConfirm) ? (
      <>
        {canSend ? (
          <Button onClick={() => markSent()} disabled={busy}>
            {send.isPending ? 'Sending…' : 'Mark as sent'}
          </Button>
        ) : null}
        {/*
          * ⚠ OQ-U3-1, carried through the restyle: `/confirm` accepts a DRAFT
          * (`quote-lifecycle.service.ts` §2). The label differs from the sent
          * path's because the act differs — this one skips a step — and the
          * dialog states what confirming a draft additionally does.
          */}
        {canConfirm ? (
          <Button variant="ghost" onClick={() => setConfirmOpen(true)} disabled={busy}>
            Confirm as an order
          </Button>
        ) : null}
        {canConfirm ? (
          <Button variant="ghost" onClick={() => setLostOpen(true)} disabled={busy}>
            Mark lost
          </Button>
        ) : null}
      </>
    ) : undefined;

  const step2Actions =
    isSent && canConfirm ? (
      <>
        <Button onClick={() => setConfirmOpen(true)} disabled={busy}>
          Confirm
        </Button>
        <Button variant="ghost" onClick={() => setLostOpen(true)} disabled={busy}>
          Mark lost
        </Button>
        {canAnnul ? (
          <Button variant="ghost" onClick={() => setAnnulOpen(true)} disabled={busy}>
            Annul
          </Button>
        ) : null}
      </>
    ) : undefined;

  /* ── the totals stack — every figure a wire string, rendered not derived ── */

  const totalsRows: TotalsRowSpec[] = [
    {
      key: 'subtotal',
      label: 'Subtotal',
      value: <ServerAmount value={quote.subtotal} currency={quote.currency} />,
    },
    {
      key: 'discount',
      label: 'Discount',
      // SIGNED — see `ServerDeduction`. The column is read downward.
      value: <ServerDeduction value={quote.discount_total} currency={quote.currency} />,
    },
    ...quote.taxes.map((tax) => ({
      key: `tax-${tax.id}`,
      label: `${tax.tax_name} @ ${tax.rate}%`,
      // The base ON SCREEN, not in a title attribute (W8) — how a reader follows
      // subtotal → discount → tax on a record, where `taxable_base` is not a column.
      detail: `on ${formatMoney(tax.base_amount, quote.currency)}`,
      value: <ServerAmount value={tax.tax_amount} currency={quote.currency} />,
    })),
    {
      key: 'tax-total',
      /*
       * `tax_total` — THE FIGURE `grand_total` WAS BUILT FROM, ALWAYS RENDERED
       * (W4, carried from `money.tsx`'s block): with no named row, a document
       * carrying a real tax total must not print nothing above a grand total
       * containing it; with rows, this line is the one the grand total is
       * answerable to. The mockup's fixture always had exactly one named row,
       * so it never met the case this row exists for.
       */
      label: 'Tax',
      ...(quote.taxes.length === 0 ? { detail: 'No tax breakdown was resolved' } : {}),
      value: <ServerAmount value={quote.tax_total} currency={quote.currency} />,
    },
  ];

  return (
    <div className="flex flex-col gap-4 p-6">
      {/*
        * The trail. record.html carries it in its mocked topbar chrome; this
        * shell has no breadcrumb bar, so the line survives on the page —
        * derived-not-drawn, recorded in the WT4 report. `text-fg`, because it
        * sits on the CANVAS, where the quiet token measures 4.10 (D3).
        */}
      <p className="text-label text-fg">
        <Link to={`/countries/${countryCode}`} className="hover:underline">
          Pipeline
        </Link>
        {' / '}
        <Link to={`/countries/${countryCode}/quotes`} className="hover:underline">
          Quotes
        </Link>
        {' / '}
        <span className="font-mono text-mono">{quote.doc_no}</span>
      </p>

      {/* ═══ the identity card — what this is ═══════════════════════════════ */}
      <RecordCard>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="min-w-0 flex-1 truncate text-h1 text-fg">{customer}</h1>
          {/* The money ADJACENT to the customer, as on the register's row — and
            * the ••• marker lands in this same slot at this same size. */}
          <ServerAmount
            value={quote.grand_total}
            currency={quote.currency}
            className="text-h1 text-fg"
          />
          <QuoteStatusPill status={quote.status} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-sm border border-line bg-subtle px-2 py-0.5 font-mono text-mono text-fg">
            {quote.doc_no}
          </span>
          {unnumberedDraft ? null : (
            <Button
              variant="ghost"
              onClick={() => {
                void copyNumber();
              }}
            >
              <Copy aria-hidden="true" className="size-4" />
              Copy number
            </Button>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* D5: the PDF acts are ABSENT without `amounts.view` (U4-1). */}
            {canPdf ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setPdfError(null);
                  render.mutate(undefined, {
                    onSuccess: (result) => {
                      onToast(
                        result.watermarked
                          ? 'Document rendered — stamped DRAFT until the quote is sent.'
                          : 'Document rendered.',
                      );
                    },
                    onError: (error) => setPdfError(quoteErrorText(error)),
                  });
                }}
                disabled={busy}
              >
                <FileText aria-hidden="true" className="size-4" />
                {render.isPending ? 'Rendering…' : 'Render PDF'}
              </Button>
            ) : null}
            {canPdf && quote.pdf_path !== null ? (
              <Button
                variant="ghost"
                onClick={() => {
                  void download();
                }}
                disabled={busy}
              >
                <Download aria-hidden="true" className="size-4" />
                {downloading ? 'Downloading…' : 'Download PDF'}
              </Button>
            ) : null}
          </div>
        </div>

        {/*
          * The sub-line ends in the map's meaning sentence — what makes an
          * ABSENT action legible: a Sales Manager who cannot find "Confirm"
          * reads here (and on the lifecycle) where the document is, rather
          * than concluding the button is broken.
          */}
        <p className="text-body13 text-fg-secondary">{provenance}</p>
      </RecordCard>

      {maskedSeat ? (
        <Alert tone="info" title="Amounts are hidden for this account">
          Every money slot on this record shows the ••• marker in place of the figure.{' '}
          {me == null
            ? ''
            : maskedRole === null
              ? `You are signed in as ${me.name}. `
              : `You are signed in as ${me.name}, ${maskedRole} for ${countryCode}. `}
          No built-in role reads quotes without amounts, so this is a per-user permission
          override and an administrator can restore it.
        </Alert>
      ) : null}

      {pdfError === null ? null : (
        <Alert tone="danger" title="The document could not be produced">
          {pdfError}
        </Alert>
      )}

      {quote.status === 'lost' ? (
        <Alert tone="warning" title="This quote was closed as lost">
          {/* OQ-U3-3 — no `lost_reason` column on `quotes`. Saying where the reason
            * IS beats an empty "Reason: —" that implies none was given, when the
            * API refuses the transition without one. */}
          A reason was required and recorded in the audit trail (the `quote.lost` event).
          There is no column on the quote itself to display it from.
        </Alert>
      ) : null}

      {quote.status === 'annulled' ? (
        <Alert tone="warning" title="This quote was annulled">
          It was voided after it was issued. It keeps its number and stays on record as annulled,
          with the reason that was recorded; nothing more can happen to it.
        </Alert>
      ) : null}

      {quote.fx_stale ? (
        <Alert tone="warning" title="The FX rate behind this quote is from an earlier period">
          The {quote.currency} figures below are the document. The group&rsquo;s INR
          reporting value uses an older rate.
        </Alert>
      ) : null}

      {/* ═══ the lifecycle card — where it is in its life ═══════════════════ */}
      <RecordCard
        flush
        title="Lifecycle"
        sub="Three states before this document is finished"
      >
        <ol className="m-0 list-none p-0">
          <LifecycleStep
            n={1}
            title="Raised"
            state={step1State}
            desc={`Raised ${formatDateLong(quote.created_at)}. ${numberingNote}`}
            actions={step1Actions}
          />
          <LifecycleStep
            n={2}
            title="Sent"
            state={step2State}
            notReached={isClosedTerminal}
            desc={step2Desc}
            actions={step2Actions}
          />
          <LifecycleStep
            n={3}
            title="Confirmed"
            state={step3State}
            notReached={isClosedTerminal}
            desc={step3Desc}
          />
        </ol>
      </RecordCard>

      {/* ═══ the cols — the lines, then the properties ══════════════════════ */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <RecordCard
            flush
            title="Lines"
            sub={`${String(quote.lines.length)} priced by the server`}
          >
            <DocumentLineTable
              frameless
              caption={`Lines on quote ${quote.doc_no}`}
              lines={quote.lines}
              currency={quote.currency}
            />
            <div className="flex flex-col items-end gap-2 border-t border-line px-4 py-3">
              <TotalsBlock
                className="w-full max-w-[320px]"
                rows={totalsRows}
                grand={{
                  label: 'Grand total',
                  value: (
                    <ServerAmount value={quote.grand_total} currency={quote.currency} />
                  ),
                }}
              />
              {/*
                * D6's own sentence: the document is indicative and the invoice
                * governs. Printed on the PDF (`quote-document.template.ts`) and
                * on screen for the same reason.
                */}
              <p className="text-label text-fg-secondary">
                Taxes as applicable; invoice values per the selling entity&rsquo;s records
                govern.
              </p>
            </div>
          </RecordCard>

          <DiscountPanel
            canRequest={canRequestDiscount}
            canApprove={canApproveDiscount}
            isDraft={isDraft}
            busy={busy}
            lastRequest={lastRequest}
            decideError={decide.isError ? quoteErrorText(decide.error) : null}
            onRequest={() => setDiscountOpen(true)}
            onDecide={(decision) =>
              decide.mutate(
                { decision },
                {
                  onSuccess: (result) => {
                    setLastRequest(result.request);
                    onToast(
                      decision === 'approve'
                        ? 'Discount approved — the quote was re-priced by the server.'
                        : 'Discount rejected — the quote is unchanged.',
                    );
                  },
                  onError: (error) => onToast(quoteErrorText(error), 'error'),
                },
              )
            }
          />
        </div>

        <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
          <RecordCard title="Document">
            <KvList>
              {/*
                * STATED FOR BOTH KINDS, unlike the list's row grammar: on the
                * record the reader is committed to this one document, and the
                * type changes what step 3 will do on confirm (§B5).
                */}
              <KvRow label="Type">{QUOTE_TYPE_LABELS[quote.type]}</KvRow>
              <KvRow label="Currency">{quote.currency}</KvRow>
              {/*
                * WHICH NAMED TAX CONFIGURATION priced this document (migration
                * 30, OD-1 — design U3). "Not recorded" is the truthful state for
                * documents priced before the cutover.
                */}
              <KvRow label="Tax group">{quote.tax_group_name ?? 'Not recorded'}</KvRow>
              {/* Honest no-value dash, `format.ts`'s own — never a blank. */}
              <KvRow label="Valid till">{formatDateLong(quote.valid_till)}</KvRow>
              {quote.legacy_ref === null ? null : (
                <KvRow label="Legacy ref" mono>
                  {quote.legacy_ref}
                </KvRow>
              )}
              {/* AGT U7 — a recovery document names the ORIGINAL it integrates (8 f), 9 g)). */}
              {quote.recovery_kind && quote.recovery_ref ? (
                <KvRow label="Recovery of" mono>
                  {`${quote.recovery_kind === 'M' ? 'Manual' : 'Duplicate'} ${quote.recovery_ref}`}
                </KvRow>
              ) : null}
            </KvList>
            {/*
              * The mockup's help line names the place-of-supply ladder RUNG; the
              * record wire does not carry the rung, so this says what the wire
              * can prove and no more (the honesty rule — a rung would be a guess).
              */}
            <p className="text-label text-fg-secondary">
              {quote.tax_group_name === null
                ? 'This document was priced before named tax groups existed, so there is no ' +
                  'group to record — the tax rows under the lines are still the server’s own figures.'
                : `The server resolved the tax rows from ${quote.tax_group_name} via the ` +
                  'place-of-supply ladder run on the customer record at pricing time; the ' +
                  'figures in the totals are its output, not a client calculation.'}
            </p>
          </RecordCard>

          <RecordCard title="Customer">
            <KvList>
              <KvRow label="Name">{quote.party_name ?? NO_VALUE}</KvRow>
              {/*
                * §B3, on the DOCUMENT — rendered from the same helper the party
                * picker uses: two screens stating the customer's country
                * differently would be worse than one staying quiet.
                */}
              {customerOriginLabel(quote.party_customer_country) === null ? null : (
                <KvRow label="Customer in">
                  {(customerOriginLabel(quote.party_customer_country) ?? '').replace(
                    'Customer in ',
                    '',
                  )}
                </KvRow>
              )}
              {quote.inquiry_id === null ? null : (
                <KvRow label="From inquiry">
                  <Link
                    to={`/countries/${countryCode}/inquiries/${quote.inquiry_id}`}
                    className="text-fg underline underline-offset-2 hover:decoration-2"
                  >
                    Open the inquiry
                  </Link>
                </KvRow>
              )}
            </KvList>
          </RecordCard>

          <RecordCard title="History">
            <KvList>
              <KvRow label="Raised">{formatDateLong(quote.created_at)}</KvRow>
              {/* The honest dash for a date that never happened — never a blank,
                * never an invented instant. */}
              <KvRow label="Sent">{formatDateLong(quote.sent_at)}</KvRow>
              <KvRow label="Confirmed">
                {quote.confirmed_at === null
                  ? 'Not confirmed'
                  : formatDateLong(quote.confirmed_at)}
              </KvRow>
            </KvList>
          </RecordCard>
        </aside>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={`Confirm ${quote.doc_no}?`}
        /*
         * THE DIALOG PROMISES WHAT WILL ACTUALLY HAPPEN. A Tally Sales quote makes the
         * transition and stops (§B5) — telling that operator "this creates a sales
         * order" would be a false statement in the one place the app asks them to
         * commit.
         *
         * ⚠ THREE BODIES NOW, BECAUSE THERE ARE THREE BEHAVIOURS (C11/IQ-12b, ruling Q1).
         * It said "Two bodies, because there are two behaviours", and the `else` arm
         * promised a sales order. THIS IS THE LAST PROMISE THE APP MAKES BEFORE THE
         * OPERATOR COMMITS, and a mixed document falling into that arm would have been
         * told an order was coming for work that will never be scheduled from it. The
         * mixed body names the withdrawn alternative explicitly, because an operator who
         * expected a part-order deserves to know it was decided against rather than
         * forgotten.
         */
        body={
          (quote.type === 'crf'
            ? 'This creates a sales order from the quote as it stands, with an immutable copy of ' +
              'every line and total. The order is created awaiting Tally sync — the push itself ' +
              'is Phase 3b and nothing is sent to Tally tonight. The quote can no longer be re-priced.'
            : quote.type === 'tally_sales'
              ? 'This marks the quote confirmed and stops there. No sales order is created for a ' +
                'Tally Sales quote — the licence is raised in Tally by hand today, and automating ' +
                'that is not in this build. The quote can no longer be re-priced.'
              : 'This marks the quote confirmed and stops there. NO sales order is created — not ' +
                'even for the customisation lines. This document sells a Tally licence alongside ' +
                'other work; the licence is raised in Tally by hand, and a part-order for the rest ' +
                'is not something this build writes. The quote can no longer be re-priced.') +
          /*
           * THE EXTRA SENTENCE A DRAFT EARNS, and it is not decoration: confirming a
           * draft stamps `sent_at` with the confirm, so the register will show this quote
           * as sent on a date nobody sent anything. Saying it here is what makes that
           * deliberate rather than a surprise found later in a report.
           */
          (isDraft
            ? ' This quote was never sent: confirming it records today as its sent date, ' +
              'because that is when the customer’s agreement was taken.'
            : '')
        }
        confirmLabel="Confirm the quote"
        busy={confirm.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => confirmNow()}
      />

      {/* DE 74/19 33 c) (AGT U2): the program ASKS the user to confirm the clock before issuing. */}
      <ConfirmModal
        open={clockRetry !== null}
        title="Confirm the date and time"
        body={clockRetry === null ? '' : clockBehindSentence(clockRetry.clock)}
        confirmLabel="Confirm and issue"
        busy={send.isPending || confirm.isPending}
        onCancel={() => setClockRetry(null)}
        onConfirm={() => (clockRetry?.act === 'confirm' ? confirmNow(true) : markSent(true))}
      />

      <LostDialog
        open={lostOpen}
        docNo={quote.doc_no}
        busy={lost.isPending}
        onCancel={() => setLostOpen(false)}
        onSubmit={(reason) =>
          lost.mutate(reason, {
            onSuccess: () => {
              setLostOpen(false);
              onToast(`${quote.doc_no} closed as lost`);
            },
            onError: (error) => onToast(quoteErrorText(error), 'error'),
          })
        }
      />

      <AnnulDialog
        open={annulOpen}
        docNo={quote.doc_no}
        busy={annul.isPending}
        onCancel={() => setAnnulOpen(false)}
        onSubmit={(reason) =>
          annul.mutate(reason, {
            onSuccess: () => {
              setAnnulOpen(false);
              onToast(`${quote.doc_no} annulled`);
            },
            onError: (error) => onToast(quoteErrorText(error), 'error'),
          })
        }
      />

      <DiscountRequestDialog
        open={discountOpen}
        docNo={quote.doc_no}
        busy={requestDiscount.isPending}
        onCancel={() => setDiscountOpen(false)}
        onSubmit={(body) =>
          requestDiscount.mutate(body, {
            onSuccess: (row) => {
              setDiscountOpen(false);
              setLastRequest(row);
              onToast(`Discount of ${body.pct}% requested on ${quote.doc_no}`);
            },
            onError: (error) => onToast(quoteErrorText(error), 'error'),
          })
        }
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────── the lifecycle step ── */

type StepState = 'done' | 'now' | 'next';

const STEP_PILL: Readonly<Record<StepState, { tone: DocTone; label: string }>> = {
  done: { tone: 'success', label: 'Done' },
  now: { tone: 'info', label: 'Current' },
  next: { tone: 'neutral', label: 'Not yet' },
};

/**
 * One numbered lifecycle step — `record.html`'s `.step` grammar.
 *
 *  · THE CURRENT STEP IS A LEFT EDGE, NOT A FILL: `status/info`'s tint would
 *    drop this card's own quiet text to ~4.1. The edge takes `info/text` in
 *    light and `info/bg` in dark — the same flip `docRailClass` measured for
 *    the record rails.
 *  · INERT IS A DASHED RIM with its words at full contrast (MC-7) — never an
 *    opacity, and the dashes sit on the NUMBER token, whose meaning they carry.
 *  · The state is never colour alone: the numbered token, the tone pill's word
 *    (`Done` / `Current` / `Not yet` — `Not reached` on a closed quote's
 *    unreached steps, because "Not yet" would promise a future a lost quote
 *    does not have), and `aria-current="step"` all say it.
 */
function LifecycleStep({
  n,
  title,
  state,
  notReached = false,
  desc,
  actions,
}: {
  n: number;
  title: string;
  state: StepState;
  /** A terminal quote's unreached step: the pill says so instead of "Not yet". */
  notReached?: boolean;
  desc: string;
  actions?: React.ReactNode;
}): React.JSX.Element {
  const pill =
    state === 'next' && notReached
      ? { tone: 'neutral' as DocTone, label: 'Not reached' }
      : STEP_PILL[state];
  return (
    <li
      aria-current={state === 'now' ? 'step' : undefined}
      className={cn(
        'flex gap-3 border-b border-line px-4 py-3 last:border-b-0',
        state === 'now' &&
          'border-l-[3px] border-l-info-fg pl-[13px] dark:border-l-info-bg',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-pill border text-label font-semibold tnum',
          state === 'done' && 'border-ok-fg bg-ok-bg text-ok-fg',
          state === 'now' && 'border-info-fg bg-info-bg text-info-fg',
          state === 'next' && 'border-dashed border-line-strong bg-transparent text-fg',
        )}
      >
        {n}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="flex items-center gap-2 text-body font-medium text-fg">
          {title}
          <span
            className={cn(
              'inline-flex items-center rounded-pill px-2 py-0.5 text-label font-medium',
              docToneClasses(pill.tone),
            )}
          >
            {pill.label}
          </span>
        </p>
        <p className="text-body13 text-fg-secondary">{desc}</p>
        {actions === undefined ? null : (
          <div className="flex flex-wrap items-center gap-2 pt-1">{actions}</div>
        )}
      </div>
    </li>
  );
}

/* ──────────────────────────────────────────────────────────────── discounts ── */

/**
 * What this app can say about discounts, which is less than it would like.
 *
 * The card renders for anybody holding either discount key; it renders NOTHING
 * for anybody holding neither. Its whole content is a statement about the limits
 * of the API surface plus whatever the two POSTs returned this session. WT4
 * dressed it in the house `RecordCard` and moved the REQUEST act in here from
 * the old header row — the act now sits beside the card that explains it.
 *
 * =============================================================================
 * BOTH SIDES ARE GATED ON `draft`, AND THAT IS A CLOSED QUESTION (W7)
 * =============================================================================
 * The request side was gated on the status from the start (OQ-6). The DECISION
 * side was gated on the permission alone, so an approver looking at a confirmed
 * quote saw Approve and Reject — two buttons whose only reachable outcome was
 * 409 `…has no pending discount request to decide`.
 *
 * That is not a judgement call about likelihood, it is derivable from two rules
 * this build already enforces:
 *  · a request can only be RAISED on a draft (`quote-discounts.service.ts`
 *    `assertDraft`, U2 §7 / OQ-6); and
 *  · U3 refuses `send`, `confirm` AND `lost` while a request is open
 *    (`quote-lifecycle.service.ts` `assertNoOpenDiscountRequest`) — precisely so
 *    a request cannot be stranded on a document nobody can re-price.
 * Together: a pending request implies a draft quote. On any other status there is
 * nothing to decide, and the card says so instead of offering the round trip.
 *
 * ABSENT, AND EXPLAINED. This is a STATE gate, not a permission gate, so the D5
 * rule applies in its second half: the controls go, and a sentence takes their
 * place saying where the document is and why that settles it.
 */
function DiscountPanel({
  canRequest,
  canApprove,
  isDraft,
  busy,
  lastRequest,
  decideError,
  onRequest,
  onDecide,
}: {
  canRequest: boolean;
  canApprove: boolean;
  isDraft: boolean;
  busy: boolean;
  lastRequest: DiscountRequestDto | null;
  decideError: string | null;
  onRequest: () => void;
  onDecide: (decision: 'approve' | 'reject') => void;
}): React.JSX.Element | null {
  if (!canRequest && !canApprove) return null;

  return (
    <RecordCard title="Discount">
      <p className="text-label text-fg-secondary">
        {/* OQ-8, stated on the screen it constrains rather than only in a report. */}
        This build has no route that LISTS discount requests — the two discount endpoints
        are both writes. So this card cannot tell you what is pending; it can only show what
        an action here returned.
      </p>

      {canRequest && isDraft ? (
        <div>
          <Button variant="ghost" onClick={onRequest} disabled={busy}>
            Request discount
          </Button>
        </div>
      ) : null}

      {!isDraft && canRequest ? (
        <Alert tone="info" title="A discount can only be requested on a draft">
          {/* OQ-6. The flow the business actually wants — the customer pushes back on a
            * quote they have been sent — is refused by this build, and that is worth
            * saying where somebody will hit it. */}
          The API refuses a request on a quote that has left draft, so the common case (the
          customer negotiating after seeing the document) is not supported yet.
        </Alert>
      ) : null}

      {!isDraft && canApprove ? (
        <Alert tone="info" title="There is no pending request this quote could have">
          {/* W7 — see the block comment above this component for the derivation. */}
          A discount can only be asked for on a draft, and this build refuses to send,
          confirm or lose a quote while a request is still open. So a pending request cannot
          survive into any other status, and both Approve and Reject would answer 409 here.
        </Alert>
      ) : null}

      {lastRequest === null ? null : (
        <div className="flex flex-col gap-1 rounded-sm border border-line bg-subtle p-3">
          <p className="text-body13 text-fg">
            {lastRequest.requested_pct === null
              ? `Amount discount requested (${lastRequest.requested_amount ?? '—'})`
              : `${lastRequest.requested_pct}% requested`}
            {' · '}
            {lastRequest.status}
          </p>
          <p className="text-label text-fg-secondary">{lastRequest.reason}</p>
          {lastRequest.requested_amount !== null && lastRequest.status === 'pending' ? (
            <p className="text-label text-fg-secondary">
              {/* OQ-4. Stored faithfully, refused at approval with 501. */}
              An amount request is stored but cannot be approved by this build — the pricing
              lever is a per-line percentage, and a fixed amount generally has no exact
              solution at DECIMAL(6,3).
            </p>
          ) : null}
        </div>
      )}

      {canApprove && isDraft ? (
        <div className="flex flex-col gap-2">
          {decideError === null ? null : <Alert tone="danger">{decideError}</Alert>}
          <div className="flex items-center gap-2">
            <Button onClick={() => onDecide('approve')} disabled={busy}>
              Approve pending request
            </Button>
            <Button variant="ghost" onClick={() => onDecide('reject')} disabled={busy}>
              Reject
            </Button>
          </div>
          <p className="text-label text-fg-secondary">
            Approving re-prices the quote on the server. The approver may not be the
            requester — the API refuses that with 403.
          </p>
        </div>
      ) : null}
    </RecordCard>
  );
}

/* ────────────────────────────────────────────────────────────────── dialogs ── */

/**
 * The mandatory lost reason.
 *
 * ⚠ WT4 MIGRATION — this and `DiscountRequestDialog` were the consumers of the
 * page's local `DialogShell` (a hand-wired Radix composition whose W3 history —
 * the div that CLAIMED modality and implemented none of it — is recorded in the
 * vendored `Modal`'s header). Both now render through `components/ui/Modal`:
 * same focus trap, Escape handler, `aria-hidden` page, scrim, busy-guard —
 * one source of truth instead of a per-page copy. The focus/escape/label pins
 * the inline shell carried live on in `quotes-record.test.tsx` unchanged.
 *
 * `features/inquiries/LostReasonDialog.tsx` is the same IDEA and not the same
 * component (typed on `StageMove`; a quote has no stage) — it stays as filed
 * (ledger item 4).
 */
function LostDialog({
  open,
  docNo,
  busy,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  docNo: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const valid = reason.trim() !== '';
  return (
    <Modal
      open={open}
      title={`Why was ${docNo} lost?`}
      description="The reason is required by the API and is recorded in the audit trail."
      busy={busy}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(reason.trim())} disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Mark as lost'}
          </Button>
        </>
      }
    >
      <TextAreaField
        label="Reason"
        rows={3}
        maxLength={200}
        value={reason}
        placeholder="e.g. Chose a competitor on price"
        onChange={(event) => setReason(event.target.value)}
      />
    </Modal>
  );
}

/**
 * Annul an issued document (AGT design §3 U2, D7) — {@link LostDialog}'s shape. The reason is
 * mandatory and at most 50 characters: the SAF-T `Reason` bound, where U6 exports it.
 */
function AnnulDialog({
  open,
  docNo,
  busy,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  docNo: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const valid = reason.trim() !== '';
  return (
    <Modal
      open={open}
      title={`Annul ${docNo}?`}
      description="The document keeps its number and is marked annulled. This cannot be undone."
      busy={busy}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(reason.trim())} disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Annul'}
          </Button>
        </>
      }
    >
      <TextAreaField
        label="Reason"
        rows={2}
        maxLength={50}
        value={reason}
        placeholder="e.g. Issued to the wrong customer"
        onChange={(event) => setReason(event.target.value)}
      />
    </Modal>
  );
}

/**
 * The discount ask. PERCENTAGE ONLY — see OQ-4 in the file header.
 *
 * `discount_pct` is DECIMAL(6,3), so the field validates to three decimals and
 * refuses over-scale input rather than letting MySQL round the ask into something
 * the requester did not type. On the vendored `Modal` since WT4 — see
 * {@link LostDialog}'s migration note.
 */
function DiscountRequestDialog({
  open,
  docNo,
  busy,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  docNo: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (body: { pct: string; reason: string }) => void;
}): React.JSX.Element {
  const [pct, setPct] = useState('');
  const [reason, setReason] = useState('');

  const trimmedPct = pct.trim();
  const pctError =
    trimmedPct === ''
      ? null
      : !/^\d{1,3}(\.\d{1,3})?$/.test(trimmedPct)
        ? 'Digits with at most three decimals, e.g. 5 or 7.5'
        : Number(trimmedPct) <= 0
          ? 'Ask for more than nothing.'
          : Number(trimmedPct) > 100
            ? 'A discount cannot exceed 100%.'
            : null;
  const valid = trimmedPct !== '' && pctError === null && reason.trim() !== '';

  return (
    <Modal
      open={open}
      title={`Request a discount on ${docNo}`}
      description="Somebody with approval authority decides. Nothing moves until they do."
      busy={busy}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({ pct: trimmedPct, reason: reason.trim() })}
            disabled={!valid || busy}
          >
            {busy ? 'Requesting…' : 'Request discount'}
          </Button>
        </>
      }
    >
      <TextField
        label="Discount %"
        inputMode="decimal"
        value={pct}
        error={pctError}
        placeholder="5"
        onChange={(event) => setPct(event.target.value)}
      />
      <TextAreaField
        label="Reason"
        rows={3}
        maxLength={300}
        value={reason}
        placeholder="Why is this warranted?"
        hint="The approver sees this and nothing else about the negotiation."
        onChange={(event) => setReason(event.target.value)}
      />
      <p className="text-label text-fg-secondary">
        A percentage, not an amount: an approved percentage is applied per line, and a fixed
        amount generally has no exact solution across independently-rounded lines (OQ-4).
      </p>
    </Modal>
  );
}
